import React, { useState, useEffect } from 'react';
import { FaBook, FaSearch, FaFilter, FaPlus, FaEdit, FaTrash, FaDownload, FaEye } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Material management component for mentors to manage their resources
 */
const MaterialManagement = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState(null);
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    type: 'document',
    gradeLevel: '',
    file: null,
    url: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const { showSuccess, showError } = useToast();
  
  // Material types options
  const materialTypes = [
    { value: 'document', label: 'Document' },
    { value: 'video', label: 'Video' },
    { value: 'resource', label: 'Resource' },
    { value: 'assignment', label: 'Assignment' },
    { value: 'quiz', label: 'Quiz' }
  ];
  
  // Category options
  const categories = [
    { value: 'math', label: 'Mathematics' },
    { value: 'science', label: 'Science' },
    { value: 'english', label: 'English' },
    { value: 'history', label: 'History' },
    { value: 'language', label: 'Foreign Languages' },
    { value: 'art', label: 'Arts & Music' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ];
  
  // Grade level options
  const gradeLevels = [
    { value: 'elementary', label: 'Elementary School' },
    { value: 'middle', label: 'Middle School' },
    { value: 'high', label: 'High School' },
    { value: 'college', label: 'College' },
    { value: 'all', label: 'All Levels' }
  ];
  
  // Fetch materials on component mount
  useEffect(() => {
    fetchMaterials();
  }, [categoryFilter, typeFilter, gradeFilter]);
  
  // Fetch materials from API
  const fetchMaterials = async () => {
    try {
      setLoading(true);
      let url = '/mentors/materials';
      
      // Build query parameters for filters
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (gradeFilter !== 'all') params.append('gradeLevel', gradeFilter);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      setMaterials(response.data.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
      showError('Failed to load materials');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'file' && files && files.length > 0) {
      setFormData({
        ...formData,
        file: files[0]
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear error for this field when user makes changes
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  // Reset form data
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      type: 'document',
      gradeLevel: '',
      file: null,
      url: '',
    });
    setFormErrors({});
  };
  
  // Handle adding new material
  const handleAddNew = () => {
    resetForm();
    setCurrentMaterial(null);
    setModalOpen(true);
  };
  
  // Handle editing existing material
  const handleEdit = (material) => {
    setCurrentMaterial(material);
    setFormData({
      title: material.title,
      description: material.description,
      category: material.category,
      type: material.type,
      gradeLevel: material.gradeLevel,
      file: null,
      url: material.url || '',
    });
    setModalOpen(true);
  };
  
  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (!formData.category.trim()) errors.category = 'Category is required';
    if (!formData.gradeLevel.trim()) errors.gradeLevel = 'Grade level is required';
    
    // Check if either file or URL is provided (unless editing an existing material)
    if (!currentMaterial && !formData.file && !formData.url.trim()) {
      errors.file = 'Either a file or URL is required';
      errors.url = 'Either a file or URL is required';
    }
    
    // Validate URL format if provided
    if (formData.url.trim() && !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.url)) {
      errors.url = 'Please enter a valid URL';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Create FormData object for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('type', formData.type);
      formDataToSend.append('gradeLevel', formData.gradeLevel);
      
      if (formData.url) {
        formDataToSend.append('url', formData.url);
      }
      
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }
      
      let response;
      
      if (currentMaterial) {
        // Update existing material
        response = await api.put(`/mentors/materials/${currentMaterial._id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Update material in state
        setMaterials(materials.map(m => 
          m._id === currentMaterial._id ? response.data.data : m
        ));
        
        showSuccess('Material updated successfully');
      } else {
        // Create new material
        response = await api.post('/mentors/materials', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Add new material to state
        setMaterials([response.data.data, ...materials]);
        showSuccess('Material created successfully');
      }
      
      // Close modal and reset form
      setModalOpen(false);
      resetForm();
      
    } catch (error) {
      console.error('Error saving material:', error);
      showError(error.response?.data?.message || 'Failed to save material');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete button click
  const handleDeleteClick = (material) => {
    setMaterialToDelete(material);
    setDeleteModalOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!materialToDelete) return;
    
    try {
      setLoading(true);
      await api.delete(`/mentors/materials/${materialToDelete._id}`);
      
      // Remove deleted material from state
      setMaterials(materials.filter(m => m._id !== materialToDelete._id));
      showSuccess('Material deleted successfully');
      setDeleteModalOpen(false);
      
    } catch (error) {
      console.error('Error deleting material:', error);
      showError('Failed to delete material');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle material preview
  const handlePreview = (material) => {
    if (material.url) {
      window.open(material.url, '_blank');
    } else if (material.fileUrl) {
      window.open(material.fileUrl, '_blank');
    }
  };
  
  // Handle material download
  const handleDownload = async (material) => {
    try {
      if (material.fileUrl) {
        // Create a link element and trigger download
        const link = document.createElement('a');
        link.href = material.fileUrl;
        link.download = material.title;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading material:', error);
      showError('Failed to download material');
    }
  };
  
  // Filter materials based on search term
  const filteredMaterials = materials.filter(material => {
    const search = searchTerm.toLowerCase();
    return (
      material.title.toLowerCase().includes(search) ||
      material.description.toLowerCase().includes(search) ||
      material.category.toLowerCase().includes(search)
    );
  });
  
  // Get icon for material type
  const getMaterialTypeIcon = (type) => {
    switch (type) {
      case 'document': return <FaBook className="text-blue-500" />;
      case 'video': return <FaEye className="text-red-500" />;
      case 'resource': return <FaDownload className="text-green-500" />;
      case 'assignment': return <FaEdit className="text-yellow-500" />;
      case 'quiz': return <FaBook className="text-purple-500" />;
      default: return <FaBook className="text-gray-500" />;
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaBook className="mr-2 text-primary" /> Material Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your teaching resources and materials
          </p>
        </div>
        
        <button
          onClick={handleAddNew}
          className="btn-primary mt-4 md:mt-0"
        >
          <FaPlus className="mr-2" /> Add New Material
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-grow relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 py-2 w-full"
              placeholder="Search materials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button
            className="btn-secondary flex items-center"
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <FaFilter className="mr-2" /> Filters
          </button>
        </div>
        
        {filtersOpen && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                className="form-select w-full"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                {materialTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                className="form-select w-full"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
              <select
                className="form-select w-full"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <option value="all">All Grades</option>
                {gradeLevels.map(grade => (
                  <option key={grade.value} value={grade.value}>
                    {grade.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      
      {/* Materials List */}
      <div className="bg-white rounded-lg shadow-soft overflow-hidden">
        {loading && materials.length === 0 ? (
          <div className="p-6 flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredMaterials.length === 0 ? (
          <div className="p-6 text-center">
            <FaBook className="mx-auto text-gray-300 text-4xl mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">No Materials Found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || categoryFilter !== 'all' || typeFilter !== 'all' || gradeFilter !== 'all'
                ? 'Try changing your search or filters'
                : 'You haven\'t created any materials yet'}
            </p>
            <button
              onClick={handleAddNew}
              className="btn-primary"
            >
              <FaPlus className="mr-2" /> Add Your First Material
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMaterials.map((material) => (
                  <tr key={material._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-gray-100">
                          {getMaterialTypeIcon(material.type)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {material.title}
                          </div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {material.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">
                        {material.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">
                        {categories.find(c => c.value === material.category)?.label || material.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">
                        {gradeLevels.find(g => g.value === material.gradeLevel)?.label || material.gradeLevel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(material.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {(material.url || material.fileUrl) && (
                          <button
                            onClick={() => handlePreview(material)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Preview"
                          >
                            <FaEye />
                          </button>
                        )}
                        
                        {material.fileUrl && (
                          <button
                            onClick={() => handleDownload(material)}
                            className="text-green-600 hover:text-green-900"
                            title="Download"
                          >
                            <FaDownload />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleEdit(material)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteClick(material)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Add/Edit Material Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={currentMaterial ? 'Edit Material' : 'Add New Material'}
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                className={`form-input w-full ${formErrors.title ? 'border-red-500' : ''}`}
                value={formData.title}
                onChange={handleChange}
              />
              {formErrors.title && (
                <p className="mt-1 text-sm text-red-500">{formErrors.title}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                rows="3"
                className={`form-textarea w-full ${formErrors.description ? 'border-red-500' : ''}`}
                value={formData.description}
                onChange={handleChange}
              ></textarea>
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-500">{formErrors.description}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  className={`form-select w-full ${formErrors.category ? 'border-red-500' : ''}`}
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {formErrors.category && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.category}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  className="form-select w-full"
                  value={formData.type}
                  onChange={handleChange}
                >
                  {materialTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grade Level <span className="text-red-500">*</span>
              </label>
              <select
                name="gradeLevel"
                className={`form-select w-full ${formErrors.gradeLevel ? 'border-red-500' : ''}`}
                value={formData.gradeLevel}
                onChange={handleChange}
              >
                <option value="">Select Grade Level</option>
                {gradeLevels.map(grade => (
                  <option key={grade.value} value={grade.value}>
                    {grade.label}
                  </option>
                ))}
              </select>
              {formErrors.gradeLevel && (
                <p className="mt-1 text-sm text-red-500">{formErrors.gradeLevel}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Upload
                  {!currentMaterial && !formData.url && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="file"
                  name="file"
                  className={`form-input w-full py-1.5 px-2 ${formErrors.file ? 'border-red-500' : ''}`}
                  onChange={handleChange}
                />
                {formErrors.file && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.file}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {currentMaterial?.fileName ? `Current file: ${currentMaterial.fileName}` : 'Supported formats: PDF, DOC, DOCX, PPT, PPTX, etc.'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Link
                  {!currentMaterial && !formData.file && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  name="url"
                  placeholder="https://"
                  className={`form-input w-full ${formErrors.url ? 'border-red-500' : ''}`}
                  value={formData.url}
                  onChange={handleChange}
                />
                {formErrors.url && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.url}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  For external resources like videos, articles, etc.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              {currentMaterial ? 'Update Material' : 'Add Material'}
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
      >
        <p className="mb-4">
          Are you sure you want to delete the material "{materialToDelete ? materialToDelete.title : ''}"? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            className="btn-secondary"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </button>
          <button
            className="btn-danger"
            onClick={handleDeleteConfirm}
            disabled={loading}
          >
            {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MaterialManagement; 